import { useState, useMemo } from 'react';
import { useCalendarEvents, useCreateEvent, useUpdateLeadFollowup, useUpdateEvent, useDeleteEvent, CalendarEvent } from '@/hooks/useCalendarEvents';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Phone,
  MessageSquare,
  Mail,
  Users,
  Target,
  Zap,
  Plus,
  Clock,
  AlertTriangle,
  Pencil,
  Trash2,
  DollarSign,
} from 'lucide-react';
import { 
  format, 
  startOfWeek, 
  addDays, 
  isSameDay, 
  isToday,
  addWeeks,
  subWeeks,
  setHours,
  setMinutes,
} from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7 AM to 8 PM

const EVENT_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  followup: { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/30' },
  meeting: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-300/50 dark:border-purple-700/50' },
  deal_milestone: { bg: 'bg-success/10', text: 'text-success', border: 'border-success/30' },
  focus_block: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-300/50 dark:border-orange-700/50' },
};

const ACTION_TYPE_ICONS: Record<string, typeof Phone> = {
  call: Phone,
  whatsapp: MessageSquare,
  email: Mail,
  meeting: Users,
  demo: Target,
};

export default function Calendar() {
  const [view, setView] = useState<'week' | 'day'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEventData, setNewEventData] = useState({
    title: '',
    type: 'meeting' as const,
    date: new Date(),
    time: '09:00',
    leadId: '',
    notes: '',
    priority: 'normal' as 'normal' | 'important' | 'urgent',
  });

  const REMINDER_PRESETS = {
    normal: [15],
    important: [60, 15],
    urgent: [1440, 60, 15, 0], // 24h, 60m, 15m, 0m
  };

  const { data: events, isLoading } = useCalendarEvents(currentDate, view);
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const createEvent = useCreateEvent();
  const updateFollowup = useUpdateLeadFollowup();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();

  // Edit event state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editEventData, setEditEventData] = useState<{
    id: string;
    title: string;
    type: 'call' | 'demo' | 'meeting' | 'fair' | 'other';
    date: Date;
    time: string;
    leadId: string;
    notes: string;
    priority: 'normal' | 'important' | 'urgent';
  } | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEvent | null>(null);

  // Get week days
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Group events by day and hour
  const eventsByDayHour = useMemo(() => {
    const map: Record<string, Record<number, CalendarEvent[]>> = {};
    
    events?.forEach(event => {
      const dayKey = format(event.start, 'yyyy-MM-dd');
      const hour = event.start.getHours();
      
      if (!map[dayKey]) map[dayKey] = {};
      if (!map[dayKey][hour]) map[dayKey][hour] = [];
      map[dayKey][hour].push(event);
    });

    return map;
  }, [events]);

  const handlePrevious = () => {
    if (view === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const handleNext = () => {
    if (view === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const handleCreateEvent = async () => {
    if (!newEventData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const [hours, minutes] = newEventData.time.split(':').map(Number);
      const startAt = setMinutes(setHours(newEventData.date, hours), minutes);
      const endAt = new Date(startAt.getTime() + 60 * 60 * 1000); // 1 hour default

      await createEvent.mutateAsync({
        title: newEventData.title,
        type: newEventData.type,
        start_at: startAt.toISOString(),
        end_at: endAt.toISOString(),
        lead_id: newEventData.leadId || undefined,
        notes: newEventData.notes || undefined,
        priority: newEventData.priority,
        reminder_offsets_minutes: REMINDER_PRESETS[newEventData.priority],
      });

      toast.success('Event created');
      setCreateDialogOpen(false);
      setNewEventData({
        title: '',
        type: 'meeting',
        date: new Date(),
        time: '09:00',
        leadId: '',
        notes: '',
        priority: 'normal',
      });
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleDragStart = (e: React.DragEvent, event: CalendarEvent) => {
    if (event.type !== 'followup') return;
    e.dataTransfer.setData('eventId', event.id);
    e.dataTransfer.setData('leadId', event.leadId || '');
  };

  const handleDrop = async (e: React.DragEvent, day: Date, hour: number) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    if (!leadId) return;

    const newDateTime = setMinutes(setHours(day, hour), 0);
    
    try {
      await updateFollowup.mutateAsync({
        leadId,
        nextActionAt: newDateTime.toISOString(),
      });
      toast.success('Follow-up rescheduled');
    } catch (error) {
      toast.error('Failed to reschedule');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Open edit dialog for a meeting event
  const handleEditEvent = (event: CalendarEvent) => {
    // Only allow editing meeting type events (from events table)
    if (event.type !== 'meeting') {
      toast.error('Only meeting events can be edited directly');
      return;
    }
    
    const eventId = event.id.replace('event-', '');
    setEditEventData({
      id: eventId,
      title: event.title,
      type: (event.actionType as any) || 'meeting',
      date: event.start,
      time: format(event.start, 'HH:mm'),
      leadId: event.leadId || '',
      notes: event.meta?.notes || '',
      priority: event.priority || 'normal',
    });
    setEditDialogOpen(true);
    setSelectedEvent(null);
  };

  const handleSaveEdit = async () => {
    if (!editEventData) return;

    try {
      const [hours, minutes] = editEventData.time.split(':').map(Number);
      const startAt = setMinutes(setHours(editEventData.date, hours), minutes);
      const endAt = new Date(startAt.getTime() + 60 * 60 * 1000);

      await updateEvent.mutateAsync({
        eventId: editEventData.id,
        updates: {
          title: editEventData.title,
          type: editEventData.type,
          start_at: startAt.toISOString(),
          end_at: endAt.toISOString(),
          lead_id: editEventData.leadId || null,
          notes: editEventData.notes,
          priority: editEventData.priority,
        },
      });

      toast.success('Event updated');
      setEditDialogOpen(false);
      setEditEventData(null);
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = (event: CalendarEvent) => {
    // For follow-up events, directly remove (no confirmation needed - simple ✕)
    if (event.id.startsWith('followup-')) {
      const eventId = event.id.replace('followup-', '');
      deleteEvent.mutate({ 
        eventId, 
        leadId: event.leadId, 
        isFollowUp: true 
      });
      toast.success('Follow-up removed');
      return;
    }
    
    // For other events, show confirmation
    setEventToDelete(event);
    setDeleteDialogOpen(true);
    setSelectedEvent(null);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;

    try {
      const eventId = eventToDelete.id.startsWith('followup-') 
        ? eventToDelete.id.replace('followup-', '')
        : eventToDelete.id.replace('event-', '');
      const isFollowUp = eventToDelete.id.startsWith('followup-');
      const leadId = eventToDelete.leadId;
      
      await deleteEvent.mutateAsync({ 
        eventId, 
        leadId, 
        isFollowUp 
      });
      toast.success('Event removed');
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } catch (error) {
      toast.error('Failed to remove event');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingState message="Loading calendar..." />
      </div>
    );
  }

  const renderEvent = (event: CalendarEvent) => {
    const colors = EVENT_COLORS[event.type];
    const Icon = ACTION_TYPE_ICONS[event.actionType || 'meeting'] || CalendarIcon;
    const isScheduled = event.status !== 'done' && event.status !== 'canceled';
    const isHighPriority = event.priority === 'urgent' || event.priority === 'important';
    const isRevenueLinked = event.type === 'deal_milestone' || (event.type === 'followup' && event.leadTemperature === 'hot');
    const isMeetingEvent = event.type === 'meeting';
    const hasHighDealValue = event.meta?.dealValue && event.meta.dealValue >= 5000;
    
    // Get deal value or risk info for this event
    let dealValue: number | null = null;
    let isAtRisk = false;
    if (event.leadId && deals) {
      const deal = deals.find(d => d.lead_id === event.leadId && d.stage !== 'won' && d.stage !== 'lost');
      if (deal) {
        dealValue = deal.value || 0;
        const lead = leads?.find(l => l.id === event.leadId);
        if (lead) {
          const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
          if (!lastContact || (event.isOverdue && isScheduled)) {
            isAtRisk = true;
          }
        }
      }
    }

    return (
      <div
        key={event.id}
        className={cn(
          'calendar-event p-1.5 rounded text-xs border relative group',
          colors.bg,
          colors.text,
          colors.border,
          event.isOverdue && isScheduled && 'ring-2 ring-destructive/50',
          event.status === 'done' && 'opacity-50',
          !isHighPriority && !isRevenueLinked && !hasHighDealValue && 'calendar-event-low-impact',
          (isRevenueLinked || hasHighDealValue) && 'calendar-event-revenue',
          event.type === 'focus_block' && 'ring-2 ring-orange-500/80 bg-orange-500/25 border-orange-500/70 shadow-xl font-semibold'
        )}
        onClick={() => handleEventClick(event)}
        draggable={event.type === 'followup'}
        onDragStart={(e) => handleDragStart(e, event)}
      >
        {/* Remove button - simple ✕ for all events */}
        <button
          onClick={(e) => { 
            e.stopPropagation(); 
            handleDeleteEvent(event); 
          }}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-background border border-border/50 shadow-sm flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors opacity-0 group-hover:opacity-100 z-10"
          aria-label="Remove event"
        >
          <span className="text-[10px] font-bold">✕</span>
        </button>

        {/* Priority indicator */}
        {event.priority && event.priority !== 'normal' && (
          <div className={cn(
            'absolute top-1 right-1 w-2 h-2 rounded-full',
            event.priority === 'urgent' && 'bg-destructive animate-pulse',
            event.priority === 'important' && 'bg-warning'
          )} />
        )}

        {/* Focus Block label - ELITE: More evident and blocking */}
        {event.type === 'focus_block' && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 bg-orange-500/30 text-orange-600 dark:text-orange-400 border-orange-500/70 font-bold shadow-md">
              Execution Block – Revenue Protection
            </Badge>
          </div>
        )}

        {/* Revenue/High-value indicator badge - Only show if no inline value */}
        {(dealValue || isAtRisk || hasHighDealValue || (isRevenueLinked && event.type === 'deal_milestone')) && event.type !== 'focus_block' && !dealValue && (
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 z-10">
            {isAtRisk ? (
              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-destructive/10 text-destructive border-destructive/30">
                Deal at risk
              </Badge>
            ) : (hasHighDealValue || (isRevenueLinked && event.type === 'deal_milestone')) ? (
              <Badge variant="outline" className="text-[9px] px-1 py-0 bg-primary/10 text-primary border-primary/30">
                <DollarSign className="w-2 h-2 mr-0.5" />
                {hasHighDealValue ? 'High value' : 'Revenue'}
              </Badge>
            ) : null}
          </div>
        )}

        <div className="flex items-center gap-1">
          {event.isOverdue && isScheduled && <AlertTriangle className="w-3 h-3 text-destructive" />}
          <Icon className="w-3 h-3 flex-shrink-0" />
          <span className={cn("truncate font-medium", event.status === 'done' && 'line-through')}>{event.leadName || event.title}</span>
        </div>
        <div className="text-[10px] opacity-70 truncate">
          {format(event.start, 'h:mm a')}
          {event.type === 'followup' && ` • ${event.actionType}`}
        </div>
        {/* Show € or risk inline for non-focus events - Single line, no overlap */}
        {(dealValue || isAtRisk) && event.type !== 'focus_block' && (
          <div className="mt-0.5 flex items-center gap-1 flex-wrap">
            {dealValue && dealValue > 0 && (
              <span className="text-[9px] font-semibold text-primary whitespace-nowrap">
                {formatCurrency(dealValue)}
              </span>
            )}
            {isAtRisk && (
              <Badge variant="outline" className="text-[8px] px-1 py-0 border-destructive/30 text-destructive whitespace-nowrap">
                At risk
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Calendar</h1>
            <p className="text-muted-foreground text-sm">
              Revenue-linked follow-ups, calls & decisions
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrevious}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleNext}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={handleToday}>
            Today
          </Button>
          <h2 className="text-lg font-semibold ml-2">
            {view === 'week' 
              ? `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], 'MMM d, yyyy')}`
              : format(currentDate, 'EEEE, MMMM d, yyyy')
            }
          </h2>
        </div>

        <div className="flex gap-1">
          <Button
            variant={view === 'week' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('week')}
          >
            Week
          </Button>
          <Button
            variant={view === 'day' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('day')}
          >
            Day
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(EVENT_COLORS).map(([type, colors]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={cn('w-3 h-3 rounded', colors.bg, 'border', colors.border)} />
            <span className="text-xs text-muted-foreground capitalize">
              {type.replace('_', ' ')}
            </span>
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {view === 'week' ? (
            <div className="min-w-[800px]">
              {/* Day Headers */}
              <div className="grid grid-cols-8 border-b">
                <div className="p-2 text-xs text-muted-foreground" />
                {weekDays.map((day) => (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      'p-2 text-center border-l',
                      isToday(day) && 'bg-primary/5'
                    )}
                  >
                    <div className="text-xs text-muted-foreground">
                      {format(day, 'EEE')}
                    </div>
                    <div className={cn(
                      'text-lg font-semibold',
                      isToday(day) && 'text-primary'
                    )}>
                      {format(day, 'd')}
                    </div>
                  </div>
                ))}
              </div>

              {/* Time Grid */}
              <div className="max-h-[600px] overflow-y-auto">
                {HOURS.map((hour) => (
                  <div key={hour} className="grid grid-cols-8 border-b min-h-[60px]">
                    <div className="p-2 text-xs text-muted-foreground text-right pr-3">
                      {format(setHours(new Date(), hour), 'h a')}
                    </div>
                    {weekDays.map((day) => {
                      const dayKey = format(day, 'yyyy-MM-dd');
                      const hourEvents = eventsByDayHour[dayKey]?.[hour] || [];

                      return (
                        <div
                          key={`${dayKey}-${hour}`}
                          className={cn(
                            'border-l p-1 space-y-1',
                            isToday(day) && 'bg-primary/5'
                          )}
                          onDrop={(e) => handleDrop(e, day, hour)}
                          onDragOver={handleDragOver}
                        >
                          {hourEvents.map(renderEvent)}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Day View
            <div>
              <div className="max-h-[600px] overflow-y-auto">
                {HOURS.map((hour) => {
                  const dayKey = format(currentDate, 'yyyy-MM-dd');
                  const hourEvents = eventsByDayHour[dayKey]?.[hour] || [];

                  return (
                    <div key={hour} className="flex border-b min-h-[80px]">
                      <div className="w-20 p-3 text-sm text-muted-foreground text-right pr-4 flex-shrink-0">
                        {format(setHours(new Date(), hour), 'h a')}
                      </div>
                      <div 
                        className="flex-1 p-2 space-y-1 border-l"
                        onDrop={(e) => handleDrop(e, currentDate, hour)}
                        onDragOver={handleDragOver}
                      >
                        {hourEvents.map((event) => {
                          const colors = EVENT_COLORS[event.type];
                          const Icon = ACTION_TYPE_ICONS[event.actionType || 'meeting'] || CalendarIcon;
                          const isScheduled = event.status !== 'done' && event.status !== 'canceled';

                          return (
                            <div
                              key={event.id}
                              className={cn(
                                'p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.01] border relative',
                                colors.bg,
                                colors.text,
                                colors.border,
                                event.isOverdue && isScheduled && 'ring-2 ring-destructive/50',
                                event.status === 'done' && 'opacity-50'
                              )}
                              onClick={() => handleEventClick(event)}
                              draggable={event.type === 'followup'}
                              onDragStart={(e) => handleDragStart(e, event)}
                            >
                              {/* Priority indicator */}
                              {event.priority && event.priority !== 'normal' && (
                                <div className={cn(
                                  'absolute top-2 right-2 w-2.5 h-2.5 rounded-full',
                                  event.priority === 'urgent' && 'bg-destructive',
                                  event.priority === 'important' && 'bg-warning'
                                )} />
                              )}
                              <div className="flex items-center gap-2">
                                {event.isOverdue && isScheduled && <AlertTriangle className="w-4 h-4 text-destructive" />}
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                <span className={cn('font-medium', event.status === 'done' && 'line-through')}>{event.title}</span>
                                {event.leadTemperature && (
                                  <Badge variant="outline" className="text-xs ml-auto">
                                    {event.leadTemperature}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs opacity-70 mt-1">
                                {format(event.start, 'h:mm a')}
                                {event.end && ` - ${format(event.end, 'h:mm a')}`}
                              </div>
                              {/* Revenue or Risk info */}
                              {(() => {
                                let dealValue: number | null = null;
                                let isAtRisk = false;
                                if (event.leadId && deals) {
                                  const deal = deals.find(d => d.lead_id === event.leadId && d.stage !== 'won' && d.stage !== 'lost');
                                  if (deal) {
                                    dealValue = deal.value || 0;
                                    const lead = leads?.find(l => l.id === event.leadId);
                                    if (lead) {
                                      const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
                                      if (!lastContact || (event.isOverdue && isScheduled)) {
                                        isAtRisk = true;
                                      }
                                    }
                                  }
                                }
                                return (dealValue || isAtRisk) ? (
                                  <div className="mt-1.5 flex items-center gap-1.5">
                                    {dealValue && dealValue > 0 && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary">
                                        {formatCurrency(dealValue)}
                                      </Badge>
                                    )}
                                    {isAtRisk && (
                                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-destructive/30 text-destructive">
                                        Deal at risk
                                      </Badge>
                                    )}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Empty state for no events */}
      {(!events || events.length === 0) && (
        <div className="mt-8">
          <EmptyState
            icon={CalendarIcon}
            title="No events scheduled"
            description="Schedule follow-ups with leads or book meetings to see them here."
            action={
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            }
          />
        </div>
      )}

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {format(selectedEvent.start, 'EEEE, MMMM d, yyyy h:mm a')}
                </span>
              </div>

              {selectedEvent.leadName && (
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{selectedEvent.leadName}</span>
                  {selectedEvent.leadTemperature && (
                    <Badge variant="outline" className="text-xs">
                      {selectedEvent.leadTemperature}
                    </Badge>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Badge className={cn(
                  EVENT_COLORS[selectedEvent.type].bg,
                  EVENT_COLORS[selectedEvent.type].text,
                )}>
                  {selectedEvent.type.replace('_', ' ')}
                </Badge>
                {selectedEvent.isOverdue && (
                  <Badge variant="destructive">Overdue</Badge>
                )}
              </div>

              {selectedEvent.meta?.notes && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">{selectedEvent.meta.notes}</p>
                </div>
              )}

              {selectedEvent.meta?.dealValue && (
                <div className="text-sm text-muted-foreground">
                  Deal value: ${selectedEvent.meta.dealValue.toLocaleString()}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {selectedEvent?.type === 'meeting' && (
              <>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleEditEvent(selectedEvent)}
                  className="flex-1 sm:flex-none"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDeleteEvent(selectedEvent)}
                  className="flex-1 sm:flex-none text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setSelectedEvent(null)} className="flex-1 sm:flex-none">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={newEventData.title}
                onChange={(e) => setNewEventData({ ...newEventData, title: e.target.value })}
                placeholder="Meeting with..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={newEventData.type}
                  onValueChange={(v: any) => setNewEventData({ ...newEventData, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="demo">Demo</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={newEventData.time}
                  onChange={(e) => setNewEventData({ ...newEventData, time: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(newEventData.date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarPicker
                    mode="single"
                    selected={newEventData.date}
                    onSelect={(d) => d && setNewEventData({ ...newEventData, date: d })}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Lead (Optional)</Label>
              <Select
                value={newEventData.leadId || 'none'}
                onValueChange={(v) => setNewEventData({ ...newEventData, leadId: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a lead" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {leads?.filter(l => l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost')
                    .map((lead) => (
                      <SelectItem key={lead.id} value={lead.id}>
                        {lead.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <div className="flex gap-1">
                {(['normal', 'important', 'urgent'] as const).map((p) => (
                  <Button
                    key={p}
                    type="button"
                    size="sm"
                    variant={newEventData.priority === p ? 'default' : 'outline'}
                    className={cn(
                      'flex-1 capitalize',
                      newEventData.priority === p && p === 'urgent' && 'bg-destructive hover:bg-destructive/90',
                      newEventData.priority === p && p === 'important' && 'bg-warning hover:bg-warning/90 text-warning-foreground'
                    )}
                    onClick={() => setNewEventData({ ...newEventData, priority: p })}
                  >
                    {p}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {newEventData.priority === 'normal' && 'Reminder 15m before'}
                {newEventData.priority === 'important' && 'Reminders 1h & 15m before + escalation if overdue'}
                {newEventData.priority === 'urgent' && 'Reminders 24h, 1h, 15m, at start + aggressive escalation'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={newEventData.notes}
                onChange={(e) => setNewEventData({ ...newEventData, notes: e.target.value })}
                placeholder="Add notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateEvent} disabled={createEvent.isPending}>
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
          </DialogHeader>
          {editEventData && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editEventData.title}
                  onChange={(e) => setEditEventData({ ...editEventData, title: e.target.value })}
                  placeholder="Meeting with..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={editEventData.type}
                    onValueChange={(v: any) => setEditEventData({ ...editEventData, type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="demo">Demo</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={editEventData.time}
                    onChange={(e) => setEditEventData({ ...editEventData, time: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {format(editEventData.date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={editEventData.date}
                      onSelect={(d) => d && setEditEventData({ ...editEventData, date: d })}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Lead (Optional)</Label>
                <Select
                  value={editEventData.leadId || 'none'}
                  onValueChange={(v) => setEditEventData({ ...editEventData, leadId: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a lead" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {leads?.filter(l => l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost')
                      .map((lead) => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {lead.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <div className="flex gap-1">
                  {(['normal', 'important', 'urgent'] as const).map((p) => (
                    <Button
                      key={p}
                      type="button"
                      size="sm"
                      variant={editEventData.priority === p ? 'default' : 'outline'}
                      className={cn(
                        'flex-1 capitalize',
                        editEventData.priority === p && p === 'urgent' && 'bg-destructive hover:bg-destructive/90',
                        editEventData.priority === p && p === 'important' && 'bg-warning hover:bg-warning/90 text-warning-foreground'
                      )}
                      onClick={() => setEditEventData({ ...editEventData, priority: p })}
                    >
                      {p}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={editEventData.notes}
                  onChange={(e) => setEditEventData({ ...editEventData, notes: e.target.value })}
                  placeholder="Add notes..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateEvent.isPending}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{eventToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setEventToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
